import { humaniseRouteTemplate, trackPageView } from "@sembark-travel/tracking"
import { useEffect, useRef } from "react"
import { matchRoutes, useLocation, type RouteObject } from "react-router"

/**
 * Sends one page view per navigation, with the route template
 * (`/trips/:tripId/quotes`) rather than the real URL and a title derived
 * from it ("Trips › Quotes") rather than the document title, so analytics
 * page dimensions stay bounded. Rendered once, inside the root route, so guest
 * and private pages both count. Search and hash changes are not views.
 */
export function PageViewTracker({ routes }: { routes: RouteObject[] }) {
	const { pathname } = useLocation()
	const lastPathname = useRef<string | null>(null)

	useEffect(() => {
		if (lastPathname.current === pathname) return
		lastPathname.current = pathname
		const template = routeTemplate(routes, pathname)
		trackPageView({
			path: template,
			title: humaniseRouteTemplate(template),
		})
	}, [routes, pathname])

	return null
}

/**
 * The matched route's template: the `path` segments of every matched route
 * joined, which for the generated pages is the same string as the `Path`
 * union in `router.ts`. Unmatched paths report as `/*`.
 */
function routeTemplate(routes: RouteObject[], pathname: string): string {
	const matches = matchRoutes(routes, { pathname }) || []
	const segments = matches
		.map((match) => match.route.path || "")
		.flatMap((path) => path.split("/"))
		.filter(Boolean)
	return "/" + segments.join("/")
}
